import { useState, useMemo } from "react";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Palette, Copy, Zap, Check, X } from "lucide-react";

const COLORBOND = [
  { name: "Monument", hex: "#323233" }, { name: "Night Sky", hex: "#2A2A2D" },
  { name: "Basalt", hex: "#6D6C6E" }, { name: "Ironstone", hex: "#3E434C" },
  { name: "Woodland Grey", hex: "#4B4C46" }, { name: "Wallaby", hex: "#7F7C78" },
  { name: "Gully", hex: "#857E73" }, { name: "Jasper", hex: "#6C5D54" },
  { name: "Shale Grey", hex: "#BDBFBA" }, { name: "Dune", hex: "#B1ADA3" },
  { name: "Evening Haze", hex: "#C5BFAE" }, { name: "Paperbark", hex: "#CABFA4" },
  { name: "Surfmist", hex: "#E4E2D5" }, { name: "Pale Eucalypt", hex: "#7C846A" },
  { name: "Cottage Green", hex: "#304C3C" }, { name: "Deep Ocean", hex: "#364152" },
  { name: "Manor Red", hex: "#5E1D0E" }, { name: "Terrain", hex: "#6A4F3B" },
];

const MATERIALS = [
  { key: "colorbond", label: "Colorbond Steel", img: "/measureup/colorbond.jpg",
    points: ["Full privacy, wind rated", "18 Colorbond colours", "10yr+ manufacturer warranty", "1.2m – 2.1m heights"] },
  { key: "aluminium_slat", label: "Aluminium Slat", img: "/measureup/aluminium_slat.jpg",
    points: ["Horizontal slat, modern look", "Powder coat or woodgrain finish", "No rust — coastal friendly", "Custom gap spacing"] },
  { key: "aluminium_custom", label: "Custom Aluminium", img: "/measureup/aluminium_custom.jpg",
    points: ["Laser-cut / decorative panels", "Fabricated in our workshop", "Matching gates & screens", "Premium street appeal"] },
  { key: "glass_pool", label: "Glass Pool Fencing", img: "/measureup/glass_pool.jpg",
    points: ["Frameless 12mm toughened glass", "Pool-compliant (NSW)", "316 stainless spigots", "Uninterrupted views"] },
  { key: "tubular", label: "Tubular Steel", img: "/measureup/tubular.jpg",
    points: ["Security & pool compliant", "Spear, flat or loop top", "Powder coated any colour", "Great for boundaries"] },
  { key: "chain_wire", label: "Chain Wire", img: "/measureup/chain_wire.jpg",
    points: ["Most cost-effective", "Galvanised or PVC coated", "Sporting & security spec", "Barbed wire options"] },
  { key: "timber", label: "Timber", img: "/measureup/timber.jpg",
    points: ["Treated pine paling", "Lap & cap or standard", "Natural look, stainable", "Quick installation"] },
];

const GATE_PROMPTS = [
  { group: "Gate type", options: ["Sliding gate", "Swing gate (single)", "Swing gate (double)", "Pedestrian gate"] },
  { group: "Automation", options: ["Motorised — yes", "Motorised — later (pre-wire now)", "Manual only"] },
  { group: "Power at gate", options: ["240V available", "Solar kit needed", "Needs electrician"] },
  { group: "Motor brand", options: ["Centsys", "FAAC", "LiftMaster", "CAME", "No preference"] },
  { group: "Access control", options: ["Remotes", "Keypad", "Intercom", "Phone app", "Safety beams"] },
];

export default function MeasureUpPack() {
  const [sel, setSel] = useState([]);

  const toggle = (item) =>
    setSel((s) => (s.includes(item) ? s.filter((x) => x !== item) : [...s, item]));

  const summary = useMemo(() => sel.join(", "), [sel]);

  const copy = async () => {
    await navigator.clipboard.writeText(`Client selections (measure-up):\n- ${sel.join("\n- ")}`);
    toast.success("Selections copied — paste into the quote or deal notes");
  };

  return (
    <div>
      <PageHeader
        title="Measure-Up Pack"
        subtitle="Show clients colours, materials and gate options on site. Tap what they like — then copy the selections into the quote."
        breadcrumb={["Operations", "Measure-Up Pack"]}
        actions={
          <Button data-testid="copy-selections-btn" onClick={copy} disabled={!sel.length} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Copy size={15} className="mr-2" /> Copy selections ({sel.length})
          </Button>
        }
      />

      <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 space-y-6 pb-24">
        {/* Selections bar */}
        {sel.length > 0 && (
          <Card className="rounded-sm p-3 industrial-shadow border-l-4 border-l-cyan-500 sticky top-2 z-20 bg-white/95 backdrop-blur" data-testid="selections-bar">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="stamp-label text-slate-500 mr-1">Client picked:</span>
              {sel.map((s) => (
                <Badge key={s} variant="outline" className="rounded-sm bg-cyan-50 text-cyan-800 border-cyan-200 cursor-pointer" onClick={() => toggle(s)}>
                  {s} <X size={10} className="ml-1" />
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Colorbond colours */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-cyan-600" />
            <h2 className="font-display font-semibold text-lg text-slate-900">Colorbond® colour range</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-2" data-testid="colour-grid">
            {COLORBOND.map((c) => {
              const key = `Colour: ${c.name}`;
              const active = sel.includes(key);
              return (
                <button key={c.name} onClick={() => toggle(key)} data-testid={`colour-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                  className={`rounded-sm border-2 overflow-hidden text-left transition-transform hover:scale-[1.03] ${active ? "border-cyan-500 ring-2 ring-cyan-300" : "border-slate-200"}`}>
                  <div className="h-14 relative" style={{ backgroundColor: c.hex }}>
                    {active && <Check size={16} className="absolute top-1 right-1 text-white drop-shadow" />}
                  </div>
                  <div className="px-1.5 py-1 text-[10px] font-semibold text-slate-700 bg-white truncate">{c.name}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Material range */}
        <section>
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">Material range</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="material-grid">
            {MATERIALS.map((m) => {
              const key = `Material: ${m.label}`;
              const active = sel.includes(key);
              return (
                <Card key={m.key} data-testid={`material-${m.key}`}
                  className={`rounded-sm overflow-hidden industrial-shadow cursor-pointer transition-all ${active ? "ring-2 ring-cyan-500" : "hover:shadow-lg"}`}
                  onClick={() => toggle(key)}>
                  <div className="relative h-40">
                    <img src={m.img} alt={m.label} className="w-full h-full object-cover" loading="lazy" />
                    {active && <div className="absolute top-2 right-2 bg-cyan-600 text-white rounded-sm p-1"><Check size={14} /></div>}
                  </div>
                  <div className="p-3">
                    <div className="font-display font-semibold text-slate-900">{m.label}</div>
                    <ul className="mt-1.5 space-y-0.5">
                      {m.points.map((p) => (
                        <li key={p} className="text-[11px] text-slate-600 flex items-start gap-1.5">
                          <span className="text-cyan-500 mt-0.5">▪</span> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Gate automation prompts */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="text-cyan-600" />
            <h2 className="font-display font-semibold text-lg text-slate-900">Gates & automation — ask the client</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">Run through these on site so the quote covers everything first time.</p>
          <Card className="rounded-sm industrial-shadow overflow-hidden">
            <div className="relative h-36 sm:h-44">
              <img src="/measureup/gate_automation.jpg" alt="Automated gate" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-3 left-4 text-white font-display font-semibold text-lg">Automated sliding & swing gates</div>
            </div>
            <div className="p-4 space-y-3" data-testid="gate-prompts">
              {GATE_PROMPTS.map((g) => (
                <div key={g.group}>
                  <div className="stamp-label text-slate-500 mb-1.5">{g.group}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.options.map((o) => {
                      const key = `${g.group}: ${o}`;
                      const active = sel.includes(key);
                      return (
                        <button key={o} onClick={() => toggle(key)}
                          data-testid={`gate-opt-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${active ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-700 border-slate-300 hover:border-cyan-400"}`}>
                          {o}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {summary && (
          <p className="text-xs text-slate-400">Summary: {summary}</p>
        )}
      </div>
    </div>
  );
}
